import { useAuth } from '@platform/auth-client';
import { Button } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import { useNavigate } from '@tanstack/react-router';
import { LogIn } from 'lucide-react';
import { useEffect, useState } from 'react';
import { withoutLocaleInHref } from '../app/locale-path.ts';
import { forgetSignInRetry, hasRetriedSignIn, markSignInRetried } from './callback-retry.ts';
import { SessionProgress } from './SessionProgress.tsx';

export const CallbackPage = () => {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    auth
      .handleCallback()
      .then((result) => {
        forgetSignInRetry();
        if (active) setReturnTo(result.returnTo);
      })
      .catch(() => {
        if (!active) return;
        if (hasRetriedSignIn()) {
          setFailed(true);
          return;
        }
        markSignInRetried();
        auth.login({ returnTo: '/', locale: i18n.language }).catch(() => {
          setFailed(true);
        });
      });
    return () => {
      active = false;
    };
  }, [auth, i18n.language]);

  useEffect(() => {
    if (returnTo === null || auth.status !== 'authenticated') return;
    void navigate({ to: withoutLocaleInHref(returnTo), replace: true });
  }, [auth.status, navigate, returnTo]);

  return (
    <SessionProgress
      stage="workspace"
      notice={
        failed
          ? {
              tone: 'failure',
              title: t('shell.loginFailedTitle'),
              description: t('shell.callbackFailed'),
              action: (
                <Button
                  icon={<LogIn size={20} aria-hidden="true" />}
                  onClick={() => void navigate({ to: '/login', search: {} })}
                >
                  {t('shell.login')}
                </Button>
              ),
            }
          : undefined
      }
    />
  );
};
